import React, { useState, useCallback, useRef } from 'react'
import { X, Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle } from 'lucide-react'
import * as XLSX from 'xlsx'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'
import { TaskService } from '@/api/generated/services/TaskService'
import type { TaskJudgmentBasisItem } from '@/api/generated/models/TaskJudgmentBasisItem'

interface JudgmentBasisBulkModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

type ModalStep = 'template' | 'upload' | 'result'

interface UploadResult {
  updated: number
  cleared: number
  notFound: string[]
  /** 서버의 현재 값과 같아 전송하지 않은 행 수 (원래 비어 있던 항목 포함) */
  unchanged: number
  /** task_id가 비어 파일에서 건너뛴 행 수 */
  skippedRows: number
}

/** 템플릿 헤더 — 업로드 시 이 이름으로 열을 찾는다 (열 순서가 바뀌어도 동작) */
const COL_TASK_ID = '과제 ID'
const COL_BUSINESS = '사업'
const COL_TASK_GROUP = '전략과제'
const COL_TASK_SUBGROUP = '실행과제'
const COL_TASK_NAME = '세부실행과제'
const COL_JUDGMENT_BASIS = '판단근거'

const TEMPLATE_HEADERS = [
  COL_TASK_ID,
  COL_BUSINESS,
  COL_TASK_GROUP,
  COL_TASK_SUBGROUP,
  COL_TASK_NAME,
  COL_JUDGMENT_BASIS,
]

/** workspace-all은 사업 단위 페이지네이션 — 전체를 받을 때까지 순회한다 */
const TEMPLATE_PAGE_SIZE = 100
const TEMPLATE_MAX_PAGES = 50

/**
 * 판단근거 일괄 저장 모달.
 *
 * 백엔드가 엑셀 템플릿/업로드를 제공하지 않으므로(JSON API만 존재),
 * 템플릿 생성과 파싱은 프론트에서 수행하고 결과만 PATCH /api/task/bulk-judgment-basis로 보낸다.
 * 부분 성공이 기본이라 not_found는 사용자에게 그대로 보여준다.
 */
const JudgmentBasisBulkModal: React.FC<JudgmentBasisBulkModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const { showSuccess, showError, showWarning } = useAlert()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<ModalStep>('template')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const resetModal = useCallback(() => {
    setStep('template')
    setSelectedFile(null)
    setIsDownloadingTemplate(false)
    setIsUploading(false)
    setUploadResult(null)
    setIsDragging(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleClose = useCallback(() => {
    resetModal()
    onClose()
  }, [resetModal, onClose])

  /** 워크스페이스의 세부실행과제 전체 (계층 + 현재 판단근거) — 템플릿 생성과 업로드 비교에 공유 */
  const fetchAllTasks = useCallback(async (workspaceId: string): Promise<string[][]> => {
    const rows: string[][] = []

    for (let page = 1; page <= TEMPLATE_MAX_PAGES; page++) {
      const response = await TaskService.appsTaskViewsListAllTaskInWorkspace({
        workspaceId,
        page,
        perPage: TEMPLATE_PAGE_SIZE,
      })

      const businesses = response.data ?? []
      businesses.forEach(business => {
        (business.task_groups ?? []).forEach(group => {
          (group.task_subgroups ?? []).forEach(subgroup => {
            (subgroup.tasks ?? []).forEach(task => {
              rows.push([
                task.id,
                business.name ?? '',
                group.name ?? '',
                subgroup.name ?? '',
                task.name ?? '',
                task.judgment_basis ?? '',
              ])
            })
          })
        })
      })

      if (!response.meta?.has_next) break
    }

    return rows
  }, [])

  // 템플릿 다운로드 — 워크스페이스의 세부실행과제 전체 + 현재 판단근거를 채운 엑셀
  const handleDownloadTemplate = useCallback(async () => {
    if (!selectedWorkspace?.id || isDownloadingTemplate) return

    setIsDownloadingTemplate(true)
    try {
      const rows = await fetchAllTasks(selectedWorkspace.id)

      if (rows.length === 0) {
        showWarning('세부실행과제가 없어 템플릿을 만들 수 없습니다.')
        return
      }

      const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...rows])
      ws['!cols'] = [
        { wch: 38 }, // 과제 ID
        { wch: 24 }, // 사업
        { wch: 24 }, // 전략과제
        { wch: 24 }, // 실행과제
        { wch: 34 }, // 세부실행과제
        { wch: 60 }, // 판단근거
      ]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '판단근거')
      XLSX.writeFile(wb, `판단근거_일괄저장_템플릿_${new Date().toISOString().split('T')[0]}.xlsx`)

      showSuccess(`템플릿을 내려받았습니다. (세부실행과제 ${rows.length}건)`)
      setStep('upload')
    } catch (error) {
      console.error('판단근거 템플릿 생성 실패:', error)
      handleApiError(error, showError, '템플릿을 만들지 못했습니다.')
    } finally {
      setIsDownloadingTemplate(false)
    }
  }, [selectedWorkspace, isDownloadingTemplate, fetchAllTasks, showSuccess, showWarning, showError])

  const validateAndSetFile = useCallback((file: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ]
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showError('엑셀 파일(.xlsx, .xls)만 업로드할 수 있습니다.')
      return
    }
    setSelectedFile(file)
  }, [showError])

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) validateAndSetFile(file)
  }, [validateAndSetFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) validateAndSetFile(file)
  }, [validateAndSetFile])

  // 엑셀 파싱 → items 구성. 헤더 이름으로 열을 찾으므로 참고용 열을 지워도 동작한다.
  const parseWorkbook = (buffer: ArrayBuffer): { items: TaskJudgmentBasisItem[]; skippedRows: number } => {
    const wb = XLSX.read(buffer, { type: 'array' })
    const sheet = wb.Sheets[wb.SheetNames[0]]
    if (!sheet) throw new Error('시트를 찾을 수 없습니다.')

    const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false, defval: '' })
    if (grid.length < 2) throw new Error('데이터 행이 없습니다.')

    const header = (grid[0] as unknown[]).map(cell => String(cell ?? '').trim())
    const idIdx = header.indexOf(COL_TASK_ID)
    const basisIdx = header.indexOf(COL_JUDGMENT_BASIS)
    if (idIdx === -1 || basisIdx === -1) {
      throw new Error(`템플릿 형식이 아닙니다. '${COL_TASK_ID}'와 '${COL_JUDGMENT_BASIS}' 열이 필요합니다.`)
    }

    const items: TaskJudgmentBasisItem[] = []
    let skippedRows = 0

    grid.slice(1).forEach(raw => {
      const row = raw as unknown[]
      const taskId = String(row[idIdx] ?? '').trim()
      if (!taskId) {
        // 과제 ID가 없는 행은 저장 대상이 아니다 (빈 줄·메모 등)
        skippedRows++
        return
      }
      // 값이 비면 빈 문자열을 보내 해당 과제의 판단근거를 삭제한다
      items.push({ task_id: taskId, judgment_basis: String(row[basisIdx] ?? '').trim() })
    })

    return { items, skippedRows }
  }

  const handleUpload = useCallback(async () => {
    if (!selectedFile || isUploading || !selectedWorkspace?.id) return

    setIsUploading(true)
    try {
      const buffer = await selectedFile.arrayBuffer()
      const { items: parsedItems, skippedRows } = parseWorkbook(buffer)

      if (parsedItems.length === 0) {
        showError('저장할 행이 없습니다. 과제 ID가 채워진 행이 있는지 확인해주세요.')
        return
      }

      // 서버의 현재 판단근거와 비교해 실제로 바뀐 행만 보낸다.
      // 원래 비어 있던 항목을 그대로 올린 경우까지 전송하면 '삭제 N건'으로 집계돼 오해를 준다.
      const currentRows = await fetchAllTasks(selectedWorkspace.id)
      const currentById = new Map(currentRows.map(row => [row[0], row[5] ?? '']))

      const items: TaskJudgmentBasisItem[] = []
      let unchanged = 0

      parsedItems.forEach(item => {
        const current = currentById.get(item.task_id)
        if (current === undefined) {
          // 워크스페이스에 없는 과제 — 값이 있을 때만 보내 not_found로 알린다
          if (item.judgment_basis) items.push(item)
          else unchanged++
          return
        }
        if (current === item.judgment_basis) {
          unchanged++
          return
        }
        items.push(item)
      })

      if (items.length === 0) {
        setUploadResult({ updated: 0, cleared: 0, notFound: [], unchanged, skippedRows })
        setStep('result')
        return
      }

      const response = await TaskService.appsTaskViewsBulkSetJudgmentBasis({
        requestBody: { items },
      })

      const notFound = response.not_found ?? []
      setUploadResult({
        updated: response.updated ?? 0,
        cleared: response.cleared ?? 0,
        notFound,
        unchanged,
        skippedRows,
      })
      setStep('result')

      if (notFound.length > 0) {
        showWarning(`${notFound.length}건은 과제를 찾지 못해 건너뛰었습니다.`)
      } else {
        showSuccess('판단근거를 저장했습니다.')
      }
      onSuccess()
    } catch (error) {
      console.error('판단근거 일괄 저장 실패:', error)
      if (error instanceof Error && !('status' in error)) {
        // 파싱 단계 오류 — 사용자에게 원인을 그대로 알린다
        showError(error.message)
      } else {
        handleApiError(error, showError, '판단근거 저장에 실패했습니다.')
      }
    } finally {
      setIsUploading(false)
    }
  }, [selectedFile, isUploading, selectedWorkspace, fetchAllTasks, showError, showWarning, showSuccess, onSuccess])

  if (!isOpen) return null

  const labelStyle: React.CSSProperties = {
    fontFamily: designTokens.typography.fontFamily.sans[0],
    fontSize: designTokens.typography.fontSize['body-medium'][0],
    color: designTokens.colors.gray[6],
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 클릭으로 닫기 — 기존 일괄 생성 모달과 동일한 구조 */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-[560px] mx-4"
        style={{ fontFamily: designTokens.typography.fontFamily.sans[0] }}
      >
        {/* 헤더 */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: designTokens.colors.gray[1] }}
        >
          <h2
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '18px',
              fontWeight: 700,
              color: designTokens.colors.gray[6],
            }}
          >
            판단근거 일괄 저장
          </h2>
          <button type="button" onClick={handleClose} aria-label="닫기" className="p-1 rounded hover:bg-black/5">
            <X className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          {/* 1단계: 템플릿 */}
          {step === 'template' && (
            <>
              <p style={labelStyle}>
                현재 워크스페이스의 세부실행과제 목록과 판단근거가 담긴 엑셀을 내려받아,
                <strong> 판단근거 </strong>열만 채워 다시 올려주세요.
              </p>
              <ul className="list-disc pl-5" style={{ ...labelStyle, fontSize: '14px', color: designTokens.colors.gray[5] }}>
                <li>과제 ID 열은 저장 기준이므로 지우거나 수정하면 안 됩니다.</li>
                <li>내용을 바꾼 행만 저장됩니다. 원래 비어 있던 항목은 그대로 둬도 됩니다.</li>
                <li>값이 있던 판단근거를 지우면 삭제됩니다.</li>
                <li>행을 지운 과제는 그대로 유지됩니다.</li>
              </ul>
              <Button
                size="medium"
                color="dark"
                onClick={handleDownloadTemplate}
                disabled={isDownloadingTemplate || !selectedWorkspace?.id}
                className="flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                {isDownloadingTemplate ? '템플릿 만드는 중...' : '템플릿 다운로드'}
              </Button>
              <button
                type="button"
                onClick={() => setStep('upload')}
                style={{ ...labelStyle, fontSize: '14px', color: designTokens.colors.gray[5] }}
                className="underline"
              >
                이미 파일이 있어요 — 바로 업로드하기
              </button>
            </>
          )}

          {/* 2단계: 업로드 */}
          {step === 'upload' && (
            <>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 rounded border-2 border-dashed py-10 cursor-pointer transition-colors"
                style={{
                  borderColor: isDragging ? designTokens.colors.primary.DEFAULT : designTokens.colors.gray[2],
                  backgroundColor: isDragging ? designTokens.colors.gray[0] : 'transparent',
                }}
              >
                {selectedFile ? (
                  <>
                    <FileSpreadsheet className="w-8 h-8" style={{ color: designTokens.colors.primary.DEFAULT }} />
                    <span style={labelStyle}>{selectedFile.name}</span>
                    <span style={{ ...labelStyle, fontSize: '13px', color: designTokens.colors.gray[4] }}>
                      다른 파일을 선택하려면 클릭하세요
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8" style={{ color: designTokens.colors.gray[4] }} />
                    <span style={labelStyle}>엑셀 파일을 끌어다 놓거나 클릭해 선택하세요</span>
                    <span style={{ ...labelStyle, fontSize: '13px', color: designTokens.colors.gray[4] }}>
                      .xlsx, .xls
                    </span>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex items-center justify-end gap-2">
                <Button size="medium" color="white" onClick={() => setStep('template')} disabled={isUploading}>
                  이전
                </Button>
                <Button size="medium" color="dark" onClick={handleUpload} disabled={!selectedFile || isUploading}>
                  {isUploading ? '저장 중...' : '저장'}
                </Button>
              </div>
            </>
          )}

          {/* 3단계: 결과 */}
          {step === 'result' && uploadResult && (
            <>
              <div className="flex items-center gap-2">
                {uploadResult.notFound.length > 0 ? (
                  <AlertTriangle className="w-6 h-6" style={{ color: designTokens.colors.warning.DEFAULT }} />
                ) : (
                  <CheckCircle className="w-6 h-6" style={{ color: designTokens.colors.success.DEFAULT }} />
                )}
                <span style={{ ...labelStyle, fontWeight: 600 }}>
                  {uploadResult.updated === 0 && uploadResult.cleared === 0
                    ? '변경된 판단근거가 없습니다.'
                    : `판단근거 ${uploadResult.updated}건을 저장했습니다.`}
                </span>
              </div>

              <div className="flex flex-col gap-0.5">
                {uploadResult.cleared > 0 && (
                  <p style={{ ...labelStyle, fontSize: '14px', color: designTokens.colors.gray[5] }}>
                    기존에 값이 있던 {uploadResult.cleared}건은 비워져 삭제되었습니다.
                  </p>
                )}
                {uploadResult.unchanged > 0 && (
                  <p style={{ ...labelStyle, fontSize: '14px', color: designTokens.colors.gray[5] }}>
                    내용이 그대로인 {uploadResult.unchanged}건은 건드리지 않았습니다.
                  </p>
                )}
                {uploadResult.skippedRows > 0 && (
                  <p style={{ ...labelStyle, fontSize: '14px', color: designTokens.colors.gray[5] }}>
                    과제 ID가 없는 {uploadResult.skippedRows}개 행은 건너뛰었습니다.
                  </p>
                )}
              </div>

              {uploadResult.notFound.length > 0 && (
                <div
                  className="rounded p-3 flex flex-col gap-1"
                  style={{
                    backgroundColor: designTokens.colors.warning.light,
                    border: `1px solid ${designTokens.colors.warning.DEFAULT}`,
                  }}
                >
                  <span style={{ ...labelStyle, fontSize: '14px', fontWeight: 600, color: designTokens.colors.warning.dark }}>
                    찾지 못한 과제 {uploadResult.notFound.length}건 (해당 항목만 건너뛰었습니다)
                  </span>
                  <div className="max-h-32 overflow-y-auto">
                    {uploadResult.notFound.map(id => (
                      <div
                        key={id}
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '13px',
                          color: designTokens.colors.gray[5],
                        }}
                      >
                        {id}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button size="medium" color="white" onClick={resetModal}>
                  다시 올리기
                </Button>
                <Button size="medium" color="dark" onClick={handleClose}>
                  닫기
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default JudgmentBasisBulkModal
